/**
 * Which language the plugin speaks, and the Intl formatters that go with it.
 *
 * cTrader chooses, not us: `lang` arrives in the launch query string (`env.ts`) and never
 * changes afterwards. There is no language picker for the same reason there is no account
 * picker — cTrader already answers that question, and a second answer is how two parts of one
 * screen come to disagree.
 *
 * So the locale is module state, set once before the first render. That is not a shortcut
 * around a React context; it is the honest shape of something that cannot change.
 */

export type Locale = 'en' | 'de'

/** Bundles we actually have. English is the source language. */
export const LOCALES = ['en', 'de'] as const

export interface ResolvedLocale {
  /** Which string bundle to read. */
  readonly locale: Locale
  /** What `Intl` is given — the full tag when we have its language, so `de-CH` gets Swiss
   * number and date conventions while sharing the German strings. */
  readonly tag: string
  /** False when `lang` named a language we do not carry. The caller reports it; see
   * `docs/12-plugin-i18n.md` — the third locale should be chosen from what cTrader actually
   * sends, not guessed at. */
  readonly known: boolean
}

const FALLBACK: ResolvedLocale = { locale: 'en', tag: 'en', known: true }

/**
 * Exact tag, then primary subtag, then English.
 *
 * Swiss German resolves to `de`: it is spoken, not written, and a Swiss German speaker reads
 * Standard German. The tag still travels to `Intl` in full, so `de-CH` keeps its own number
 * grouping and date order.
 */
export function resolveLocale(lang: string | null | undefined): ResolvedLocale {
  const raw = (lang ?? '').trim()
  if (raw === '') return FALLBACK

  const primary = raw.split(/[-_]/)[0]?.toLowerCase() ?? ''
  const locale = LOCALES.find((l) => l === primary)
  if (!locale) return { ...FALLBACK, known: false }

  return { locale, tag: usableTag(raw) ?? locale, known: true }
}

/** A tag Intl will not throw on. cTrader's value is not ours to trust the shape of. */
function usableTag(tag: string): string | null {
  try {
    return Intl.NumberFormat.supportedLocalesOf([tag.replace(/_/g, '-')]).length > 0
      ? tag.replace(/_/g, '-')
      : null
  } catch {
    return null
  }
}

/**
 * The formatters, built once per locale. Constructing an `Intl.NumberFormat` per render is
 * the slow way to do this, and this plugin is judged on how fast it feels.
 */
export interface Formats {
  /** Amounts: always two decimals. Deliberately `decimal`, not `currency` — the journal's
   * trades carry no currency, so a currency style there would be a guess. */
  readonly money: Intl.NumberFormat
  readonly integer: Intl.NumberFormat
  readonly upTo1: Intl.NumberFormat
  readonly upTo2: Intl.NumberFormat
  readonly dateTime: Intl.DateTimeFormat
  /** English and German both use `one` and `other`; built once because it is asked on every
   * keystroke of the ticket. */
  readonly plural: Intl.PluralRules
}

function build(tag: string): Formats {
  return {
    money: new Intl.NumberFormat(tag, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    integer: new Intl.NumberFormat(tag, { maximumFractionDigits: 0 }),
    upTo1: new Intl.NumberFormat(tag, { maximumFractionDigits: 1 }),
    upTo2: new Intl.NumberFormat(tag, { maximumFractionDigits: 2 }),
    dateTime: new Intl.DateTimeFormat(tag, { dateStyle: 'short', timeStyle: 'short' }),
    plural: new Intl.PluralRules(tag),
  }
}

let active: ResolvedLocale = FALLBACK
let formatters: Formats = build(FALLBACK.tag)

/** Called once, before the first render. Returns what it settled on, so the caller can report
 * an unknown tag. */
export function setLocale(lang: string | null | undefined): ResolvedLocale {
  active = resolveLocale(lang)
  formatters = build(active.tag)
  return active
}

export function activeLocale(): ResolvedLocale {
  return active
}

export function formats(): Formats {
  return formatters
}

/**
 * Reads a number a human typed.
 *
 * `inputMode="decimal"` offers a comma on a German keyboard, and `Number('0,5')` is `NaN` —
 * which the ticket rendered as "set a stop — the size comes from it", with no size and no
 * reason given. This is the only place input is localised; a full locale-aware parser is not
 * worth it, and `de-CH` types a dot anyway.
 *
 * Empty is `NaN`, not zero. `Number('')` being 0 is how an emptied limit field could save
 * itself as a zero limit without ever being flagged as bad.
 */
export function decimal(text: string): number {
  const s = text.trim()
  if (s === '') return Number.NaN

  const comma = s.lastIndexOf(',')
  if (comma === -1) return Number(s)

  // A comma after the last dot is the decimal mark, and any dot before it is grouping;
  // the other way round the commas are grouping.
  return comma > s.lastIndexOf('.')
    ? Number(s.replace(/\./g, '').replace(',', '.'))
    : Number(s.replace(/,/g, ''))
}
